/**
 * guideErrors.ts: why a guide call failed, and how a screen shows it.
 *
 * Kept apart from claude.ts (the network call) on purpose: the screens need these
 * pure helpers to draw an error, and a test that replaces `callClaude` with a
 * stand-in must not also replace them.
 */

/**
 * Why a guide call failed, as a short code the screens translate.
 *
 * The error screens used to print the raw error text under "something went
 * wrong": "Empty response from guide", an HTTP status line, the server's own
 * wording. A Polish reader saw English there (#70). Now a failure carries a code
 * and the screen shows the catalogue line for it, `errors.guide.<code>`, in the
 * language being read. The raw text stays on the result for logs and debugging.
 *
 * Add a code here and the same key to BOTH locale files; the key-parity test
 * fails until you do.
 */
export type GuideErrorCode =
  | 'timeout'        // gave up waiting (GUIDE_TIMEOUT_MS)
  | 'network'        // the request never completed
  | 'server'         // the function or the model failed (5xx)
  | 'empty'          // it answered with nothing
  | 'rate_limited'   // too many requests in a short window
  | 'daily_limit'    // the day's call budget is used up
  | 'session_limit'  // free tier: all sessions used
  | 'cooldown'       // free tier: the wait between sessions
  | 'session_active' // another session is already in progress
  | 'signed_out'     // no valid sign-in on the request
  | 'rejected'       // the request itself was refused as malformed or too long
  | 'unknown'

/** Every code, for the places that need to check a string is one. */
export const GUIDE_ERROR_CODES: readonly GuideErrorCode[] = [
  'timeout', 'network', 'server', 'empty', 'rate_limited', 'daily_limit',
  'session_limit', 'cooldown', 'session_active', 'signed_out', 'rejected', 'unknown',
]

/**
 * The catalogue key to show for a failure. Takes whatever a screen has (a code,
 * nothing, or something unexpected) and always returns a key that exists.
 */
export function guideErrorKey(code: string | null | undefined): string {
  return `errors.guide.${GUIDE_ERROR_CODES.includes(code as GuideErrorCode) ? code : 'unknown'}`
}

/** The code on a failed result; 'unknown' when the result has none. */
export function errorCodeOf(result: { error: string; code?: string }): GuideErrorCode {
  return GUIDE_ERROR_CODES.includes(result.code as GuideErrorCode) ? (result.code as GuideErrorCode) : 'unknown'
}

/**
 * Turn a non-OK reply into a code. The facilitate function answers with a status
 * and, for its own gates, a fixed `error` word ('daily_limit_reached',
 * 'session_limit_reached', 'cooldown_active', 'session_already_active').
 */
export function codeForFailure(status: number, serverError: unknown): GuideErrorCode {
  const word = typeof serverError === 'string' ? serverError : ''
  if (word === 'daily_limit_reached') return 'daily_limit'
  if (word === 'session_limit_reached') return 'session_limit'
  if (word === 'cooldown_active') return 'cooldown'
  if (word === 'session_already_active') return 'session_active'
  // The session named in the request is not this person's, or not a session.
  if (word === 'forbidden' || status === 403) return 'rejected'
  if (status === 401) return 'signed_out'
  if (status === 429) return 'rate_limited'
  if (status === 400 || status === 413) return 'rejected'
  if (status >= 500) return 'server'
  return 'unknown'
}
